import { useState } from "react";
import { Link } from "react-router-dom";
import { fmtNumber } from "@/lib/format";
import type { CampaignChannelResponse, DeliverableItem } from "@/lib/api";

// v2.4.3 — sortable ranking columns. The original "fixed to views DESC"
// rule was the right default but missed common operator queries: "which
// KOLs are most cost-efficient" (CPM ASC) or "which KOLs underdelivered"
// (deliverable count ASC). Click any header to toggle.
type SortKey = "views" | "deliverableCount" | "likes" | "comments" | "engagement" | "spend" | "cpm";
type SortDir = "asc" | "desc";

// Default direction per column matches the typical operator question:
//   views / deliverables / likes / comments → "who's biggest"   → desc
//   engagement                                → "who's stickiest" → desc
//   spend                                     → "who cost most"   → desc
//   cpm                                       → "who's cheapest"  → asc
const DEFAULT_DIR: Record<SortKey, SortDir> = {
  views: "desc",
  deliverableCount: "desc",
  likes: "desc",
  comments: "desc",
  engagement: "desc",
  spend: "desc",
  cpm: "asc",
};

// CampaignPerformanceSummary — v1.4 — top-of-page rollup numbers + per-KOL
// performance ranking. All client-side aggregation from the deliverables
// list we already fetched; no extra round-trip.
//
// v2.4.0 — extracted from CampaignDetail.tsx (was 1299 LOC, this carries
// ~220 of those lines on its own). Pure presentational + derived data; no
// mutations, no fetching, no shared state with the parent. The data flow
// in is `channels + deliverables + spendByCurrency`, the output is JSX.
export function CampaignPerformanceSummary({
  channelCount,
  deliverables,
  channels,
  spendByCurrency,
}: {
  channelCount: number;
  deliverables: DeliverableItem[];
  channels: CampaignChannelResponse[];
  spendByCurrency: Record<string, number> | undefined;
}) {
  // Only count confirmed deliverables in the rollup — pending stuff isn't
  // verified yet and would inflate the numbers.
  const confirmed = deliverables.filter((d) => d.review_status === "confirmed");
  const totalViews    = confirmed.reduce((a, d) => a + (d.view_count    ?? 0), 0);
  const totalLikes    = confirmed.reduce((a, d) => a + (d.like_count    ?? 0), 0);
  const totalComments = confirmed.reduce((a, d) => a + (d.comment_count ?? 0), 0);
  // Weighted engagement = (likes + comments) / views. Avoids per-row
  // 0/0 NaN by guarding totalViews.
  const engagementRate = totalViews > 0
    ? ((totalLikes + totalComments) / totalViews) * 100
    : null;
  const channelsWithDelivery = new Set(confirmed.map((d) => d.video_channel_id)).size;

  // v1.4.1 — spend + CPM. spendByCurrency may have 1 currency (clean) or
  // multiple (mixed); when multiple, CPM is ambiguous and we show "—".
  const currencies = Object.keys(spendByCurrency ?? {});
  const singleCurrency = currencies.length === 1 ? currencies[0] : null;
  const totalSpend = singleCurrency && spendByCurrency ? spendByCurrency[singleCurrency] : null;
  const cpm = totalSpend !== null && totalViews > 0
    ? (totalSpend * 1000) / totalViews
    : null;
  const spendDisplay = (() => {
    if (!spendByCurrency || Object.keys(spendByCurrency).length === 0) return "—";
    return Object.entries(spendByCurrency)
      .map(([cur, amt]) => `${fmtNumber(amt)} ${cur}`)
      .join(" + ");
  })();

  // Per-KOL aggregation for the ranking table.
  type ChannelPerf = {
    channelID: number;
    channelName: string;
    deliverableCount: number;
    views: number;
    likes: number;
    comments: number;
    engagement: number | null;
    // v1.4.1 — spend + CPM per KOL. spend is plaintext deal_amount from
    // the cc row (decrypted server-side); cpm = spend × 1000 / views.
    spend: number | null;
    currency: string | null;
    cpm: number | null;
  };
  const perfByChannel = new Map<number, ChannelPerf>();
  // Seed with all KOLs (so the ranking shows 0-delivery KOLs too).
  for (const ch of channels) {
    perfByChannel.set(ch.channel_id, {
      channelID:        ch.channel_id,
      channelName:      ch.channel_name ?? ch.channel_handle ?? `#${ch.channel_id}`,
      deliverableCount: 0,
      views: 0, likes: 0, comments: 0,
      engagement: null,
      spend: ch.deal_amount ?? null,
      currency: ch.payment_currency ?? null,
      cpm: null,
    });
  }
  for (const d of confirmed) {
    const p = perfByChannel.get(d.video_channel_id);
    if (!p) continue;
    p.deliverableCount += 1;
    p.views    += d.view_count    ?? 0;
    p.likes    += d.like_count    ?? 0;
    p.comments += d.comment_count ?? 0;
  }
  for (const p of perfByChannel.values()) {
    p.engagement = p.views > 0 ? ((p.likes + p.comments) / p.views) * 100 : null;
    p.cpm = (p.spend !== null && p.views > 0) ? (p.spend * 1000) / p.views : null;
  }

  const [sortKey, setSortKey] = useState<SortKey>("views");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  // Toggle: click the active column to flip direction; click another column
  // to switch + reset direction to that column's default.
  const onSort = (k: SortKey) => {
    if (sortKey === k) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(k);
      setSortDir(DEFAULT_DIR[k]);
    }
  };

  // Null/missing values always sink to the bottom regardless of direction,
  // so a KOL with no data doesn't pretend to be "lowest CPM" or "highest
  // engagement". Numeric -Infinity / +Infinity sentinels keep the sort
  // comparator straightforward.
  const ranking = [...perfByChannel.values()].sort((a, b) => {
    const av = a[sortKey];
    const bv = b[sortKey];
    const an = av === null || av === undefined;
    const bn = bv === null || bv === undefined;
    if (an && bn) return 0;
    if (an) return 1;
    if (bn) return -1;
    const cmp = (av as number) - (bv as number);
    return sortDir === "asc" ? cmp : -cmp;
  });

  return (
    <section className="space-y-4">
      {/* Rollup numbers */}
      <div className="rounded-md border border-border bg-background p-4">
        <h2 className="mb-3 text-sm font-semibold">投放汇总</h2>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
          <StatCard
            label="已交付 / 总 KOL"
            value={`${channelsWithDelivery} / ${channelCount}`}
            sub={`${confirmed.length} 个内容已确认`}
          />
          <StatCard
            label="累计观看"
            value={fmtNumber(totalViews) ?? "—"}
            sub={`平均 ${confirmed.length > 0 ? fmtNumber(Math.round(totalViews / confirmed.length)) : "—"} / 内容`}
          />
          <StatCard
            label="累计互动"
            value={fmtNumber(totalLikes + totalComments) ?? "—"}
            sub={`${fmtNumber(totalLikes) ?? 0} 赞 · ${fmtNumber(totalComments) ?? 0} 评`}
          />
          <StatCard
            label="加权互动率"
            value={engagementRate !== null ? `${engagementRate.toFixed(2)}%` : "—"}
            sub="(赞+评) / 观看"
          />
          <StatCard
            label="总花费"
            value={spendDisplay}
            sub={
              currencies.length > 1
                ? "多币种，未折算"
                : confirmed.length > 0 && totalSpend !== null
                  ? `平均 ${fmtNumber(Math.round(totalSpend / confirmed.length))} / 内容`
                  : "解密自 deal_amount"
            }
          />
          <StatCard
            label="CPM"
            value={
              cpm !== null
                ? `${singleCurrency === "USD" ? "$" : ""}${cpm.toFixed(2)}${
                    singleCurrency !== "USD" ? ` ${singleCurrency}` : ""
                  }`
                : "—"
            }
            sub={
              cpm !== null
                ? `每千次观看成本`
                : currencies.length > 1
                  ? "多币种无法计算"
                  : "需要金额 + 观看"
            }
          />
        </div>
      </div>

      {/* Ranking */}
      {ranking.length > 0 && (
        <div className="rounded-md border border-border bg-background p-4">
          <h2 className="mb-3 text-sm font-semibold">
            KOL 表现排名
            <span className="ml-2 text-xs font-normal text-muted-foreground">
              点表头切换排序 — 当前 {SORT_LABEL[sortKey]} {sortDir === "asc" ? "↑" : "↓"}
            </span>
          </h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-[10px] uppercase tracking-wide text-muted-foreground">
                <th className="py-1 pr-2 font-medium">#</th>
                <th className="py-1 pr-2 font-medium">KOL</th>
                <SortHeader k="deliverableCount" sortKey={sortKey} sortDir={sortDir} onClick={onSort}>内容</SortHeader>
                <SortHeader k="views" sortKey={sortKey} sortDir={sortDir} onClick={onSort}>观看</SortHeader>
                <SortHeader k="likes" sortKey={sortKey} sortDir={sortDir} onClick={onSort}>点赞</SortHeader>
                <SortHeader k="comments" sortKey={sortKey} sortDir={sortDir} onClick={onSort}>评论</SortHeader>
                <SortHeader k="engagement" sortKey={sortKey} sortDir={sortDir} onClick={onSort}>互动率</SortHeader>
                <SortHeader k="spend" sortKey={sortKey} sortDir={sortDir} onClick={onSort}>花费</SortHeader>
                <SortHeader k="cpm" sortKey={sortKey} sortDir={sortDir} onClick={onSort} last>CPM</SortHeader>
              </tr>
            </thead>
            <tbody>
              {ranking.map((p, i) => (
                <tr key={p.channelID} className="border-b border-border/60 last:border-b-0">
                  <td className="py-2 pr-2 text-muted-foreground tabular-nums">{i + 1}</td>
                  <td className="py-2 pr-2">
                    <Link
                      to={`/channels/${p.channelID}`}
                      className="text-primary hover:underline"
                    >
                      {p.channelName}
                    </Link>
                  </td>
                  <td className="py-2 pr-2 text-right tabular-nums">{p.deliverableCount}</td>
                  <td className="py-2 pr-2 text-right tabular-nums font-medium">
                    {p.views > 0 ? fmtNumber(p.views) : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="py-2 pr-2 text-right tabular-nums text-muted-foreground">
                    {p.likes > 0 ? fmtNumber(p.likes) : "—"}
                  </td>
                  <td className="py-2 pr-2 text-right tabular-nums text-muted-foreground">
                    {p.comments > 0 ? fmtNumber(p.comments) : "—"}
                  </td>
                  <td className="py-2 pr-2 text-right tabular-nums">
                    {p.engagement !== null ? `${p.engagement.toFixed(2)}%` : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="py-2 pr-2 text-right tabular-nums text-muted-foreground">
                    {p.spend !== null
                      ? `${p.currency === "USD" ? "$" : ""}${fmtNumber(p.spend)}${p.currency && p.currency !== "USD" ? ` ${p.currency}` : ""}`
                      : "—"}
                  </td>
                  <td className="py-2 text-right tabular-nums">
                    {p.cpm !== null
                      ? `${p.currency === "USD" ? "$" : ""}${p.cpm.toFixed(2)}${p.currency && p.currency !== "USD" ? ` ${p.currency}` : ""}`
                      : <span className="text-muted-foreground">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

const SORT_LABEL: Record<SortKey, string> = {
  views: "观看",
  deliverableCount: "内容数",
  likes: "点赞",
  comments: "评论",
  engagement: "互动率",
  spend: "花费",
  cpm: "CPM",
};

// SortHeader renders one clickable <th> with an arrow indicator. Right-
// aligned columns (everything except KOL name) get text-right; `last`
// trims the right padding to match the original layout.
function SortHeader({
  k,
  sortKey,
  sortDir,
  onClick,
  children,
  last,
}: {
  k: SortKey;
  sortKey: SortKey;
  sortDir: SortDir;
  onClick: (k: SortKey) => void;
  children: React.ReactNode;
  last?: boolean;
}) {
  const active = sortKey === k;
  return (
    <th
      onClick={() => onClick(k)}
      className={`cursor-pointer select-none text-right font-medium hover:text-foreground ${
        last ? "py-1" : "py-1 pr-2"
      } ${active ? "text-foreground" : ""}`}
      title={`按${SORT_LABEL[k]}排序`}
    >
      {children}
      {active && <span className="ml-1">{sortDir === "asc" ? "↑" : "↓"}</span>}
    </th>
  );
}

function StatCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="rounded-md border border-border/60 bg-muted/20 p-3">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 text-xl font-semibold tabular-nums">{value}</div>
      {sub && <div className="mt-0.5 text-[10px] text-muted-foreground">{sub}</div>}
    </div>
  );
}
